// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { 
  Leaf, 
  Droplets, 
  Users, 
  GraduationCap, 
  Utensils, 
  Factory, 
  Globe, 
  Info, 
  Trash2,
  TrendingUp,
  Wind,
  Sparkles,
  Copy,
  Check,
  Loader2,
  RefreshCw,
  AlertCircle,
  Settings,
  X,
  ChevronDown,
  ChevronUp,
  Briefcase
} from 'lucide-react';

/* --- Fonts & Styles --- */
const FontStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');
    
    .font-brand { font-family: 'Proxima Nova', 'Poppins', sans-serif; }
    
    ::-webkit-scrollbar { width: 8px; }
    ::-webkit-scrollbar-track { background: #f2f4ea; }
    ::-webkit-scrollbar-thumb { background: #5e823f; border-radius: 4px; }
    ::-webkit-scrollbar-thumb:hover { background: #214300; }
  `}</style>
);

/* --- Brand Palette --- */
const BRAND = {
  primary: "#214300",    // Dark Forest Green
  secondary: "#5e823f",  // Olive Green
  lime: "#dbffba",       // Light Lime
  cream: "#f2f4ea",      // Main Background
  white: "#ffffff",      // Card Background
  turquoise: "#57bbb9",
  sage: "#c5d2bc"
};

/* --- DATA (Triple-Checked for 120g Unit Weight) --- */
const RAW_DATA = {
  activeFarms: 421,
  yield: 14241292, 
  freshFood: 1708955, // 1.7M kg
  servings: 46187973, // 46.2M
  students: 461367,
  meals: 37246456,
  // Jobs removed as requested
  waterSaved: 393059650, // 393M L
  wasteAvoided: 683582, // 684k kg
  foodMiles: 10200000,  // 10.2M km
  emissions: 1660000    // 1.66M kg CO2e
};

/* --- ANALOGY DATABASE (Dual System: Metric + Imperial) --- */
const ANALOGIES = {
  freshFood: [
    // --- CANADIAN ---
    { 
      metric: "Our 1.7M kg harvest weighs the same as 10 million official hockey pucks. That's enough for every player in history to take a slapshot. 🏒", 
      imperial: "Our 3.8M lb harvest weighs the same as 10 million official hockey pucks. That's enough for every player in history to take a slapshot. 🏒",
      theme: "canadian", scale: "micro" 
    },
    { 
      metric: "With 1.7M kg of fresh food, we've produced the equivalent weight of 85,000 curling stones. Hurry hard to the dinner table! 🥌",
      imperial: "With 3.8M lbs of fresh food, we've produced the equivalent weight of 85,000 curling stones. Hurry hard to the dinner table! 🥌",
      theme: "canadian", scale: "medium" 
    },
    { 
      metric: "Our total harvest (1.7M kg) weighs as much as 3,400 full-grown Bull Moose (meese?). That's the most Canadian weight measurement possible. 🫎",
      imperial: "Our total harvest (3.8M lbs) weighs as much as 3,400 full-grown Bull Moose (meese?). That's the most Canadian weight measurement possible. 🫎",
      theme: "canadian", scale: "macro" 
    },
    { 
      metric: "We grew 1.7M kg of greens, which weighs the same as 4.2 million large Poutines. Except ours is a little lighter on the gravy. 🍟",
      imperial: "We grew 3.8M lbs of greens, which weighs the same as 4.2 million large Poutines. Except ours is a little lighter on the gravy. 🍟",
      theme: "canadian", scale: "micro" 
    },
    { 
      metric: "That's 1.7M kg of food, equivalent to the weight of 285,000 Canada Geese. And significantly friendlier than them, too. 🦢",
      imperial: "That's 3.8M lbs of food, equivalent to the weight of 285,000 Canada Geese. And significantly friendlier than them, too. 🦢",
      theme: "canadian", scale: "medium" 
    },
    { 
      metric: "Our production weighs as much as 570 fully loaded Zambonis smoothing the ice. Now that's a clean sheet for food security. 🚜",
      imperial: "Our production weighs as much as 570 fully loaded Zambonis smoothing the ice. Now that's a clean sheet for food security. 🚜",
      theme: "canadian", scale: "macro" 
    },
    { 
      metric: "1.7M kg is equivalent to 8.5 million boxes of KD. But you don't need ketchup to enjoy our greens. 🧀",
      imperial: "3.8M lbs is equivalent to 8.5 million boxes of KD. But you don't need ketchup to enjoy our greens. 🧀",
      theme: "canadian", scale: "micro" 
    },
    { 
      metric: "That's the weight of 125 million Timbits! We'd recommend the Birthday Cake ones, but our greens are healthier. 🍩",
      imperial: "That's the weight of 125 million Timbits! We'd recommend the Birthday Cake ones, but our greens are healthier. 🍩",
      theme: "canadian", scale: "medium" 
    },
    { 
      metric: "If you stacked 1.7M kg worth of Toonies, the pile would reach past the ISS (490km high)! Space currency? 🪙",
      imperial: "If you stacked 3.8M lbs worth of Toonies, the pile would reach past the ISS (300 miles high)! Space currency? 🪙",
      theme: "canadian", scale: "macro" 
    },
    { 
      metric: "We produced 1.7M kg of food, the weight of 1.2 million bottles of Maple Syrup. Sweet success! 🍁",
      imperial: "We produced 3.8M lbs of food, the weight of 1.2 million bottles of Maple Syrup. Sweet success! 🍁",
      theme: "canadian", scale: "micro" 
    },

    // --- POP CULTURE ---
    { 
      metric: "Our 1.7M kg harvest weighs the same as 850,000 Oscar statuettes. We'd like to thank the Academy and our growers. 🏆",
      imperial: "Our 3.8M lb harvest weighs the same as 850,000 Oscar statuettes. We'd like to thank the Academy and our growers. 🏆",
      theme: "popCulture", scale: "micro" 
    },
    { 
      metric: "That's 1.7M kg of greens, or 110,000 Stanley Cups! Lord Stanley would be proud of that haul. 🏆",
      imperial: "That's 3.8M lbs of greens, or 110,000 Stanley Cups! Lord Stanley would be proud of that haul. 🏆",
      theme: "popCulture", scale: "medium" 
    },
    { 
      metric: "We grew 1.7M kg of produce—heavier than the Statue of Liberty... 8 times over! 🗽",
      imperial: "We grew 3.8M lbs of produce—heavier than the Statue of Liberty... 8 times over! 🗽",
      theme: "popCulture", scale: "macro" 
    },
    { 
      metric: "Equivalent to 8.5 million iPhones stacked up. And unlike your battery life, our freshness lasts. 📱",
      imperial: "Equivalent to 8.5 million iPhones stacked up. And unlike your battery life, our freshness lasts. 📱",
      theme: "popCulture", scale: "micro" 
    },
    { 
      metric: "That's the weight of 3,500 Concert Grand Pianos. That's music to our ears. 🎹",
      imperial: "That's the weight of 3,500 Concert Grand Pianos. That's music to our ears. 🎹",
      theme: "popCulture", scale: "medium" 
    },
    { 
      metric: "Our harvest is heavier than the space shuttle Endeavour (fully fueled)! Houston, we have a salad. 🚀",
      imperial: "Our harvest is heavier than the space shuttle Endeavour (fully fueled)! Houston, we have a salad. 🚀",
      theme: "popCulture", scale: "macro" 
    },
    { 
      metric: "1.7M kg is the weight of 4 million Vinyl Records. It sounds better on vinyl, but tastes better hydroponic. 🎵",
      imperial: "3.8M lbs is the weight of 4 million Vinyl Records. It sounds better on vinyl, but tastes better hydroponic. 🎵",
      theme: "popCulture", scale: "micro" 
    },
    { 
      metric: "Heavier than the Millennium Falcon x 50. It can make the Kessel Run in less than 12 parsecs. 🛸",
      imperial: "Heavier than the Millennium Falcon x 50. It can make the Kessel Run in less than 12 parsecs. 🛸",
      theme: "popCulture", scale: "medium" 
    },
    { 
      metric: "That's the weight of 340,000 Internet-famous Cats. I can haz cheezburger? No, have a salad. 🐱",
      imperial: "That's the weight of 340,000 Internet-famous Cats. I can haz cheezburger? No, have a salad. 🐱",
      theme: "popCulture", scale: "medium" 
    },
    { 
      metric: "Heavier than the Titanic's anchor... 60 times over. And this ship is unsinkable. ⚓",
      imperial: "Heavier than the Titanic's anchor... 60 times over. And this ship is unsinkable. ⚓",
      theme: "popCulture", scale: "macro" 
    },

    // --- GLOBAL ---
    { 
      metric: "We grew 1.7M kg of food, the weight of 34,000 bags of cement. Building a foundation for food security. 🏗️",
      imperial: "We grew 3.8M lbs of food, the weight of 34,000 bags of cement. Building a foundation for food security. 🏗️",
      theme: "global", scale: "micro" 
    },
    { 
      metric: "Equivalent to 1,200 compact cars stacked on a scale. That's a serious traffic jam of lettuce. 🚗",
      imperial: "Equivalent to 1,200 compact cars stacked on a scale. That's a serious traffic jam of lettuce. 🚗",
      theme: "global", scale: "medium" 
    },
    { 
      metric: "That's the weight of 11 Blue Whales, the largest animal on Earth. Production at a whale of a scale! 🐋",
      imperial: "That's the weight of 11 Blue Whales, the largest animal on Earth. Production at a whale of a scale! 🐋",
      theme: "global", scale: "macro" 
    },
    { 
      metric: "1.7M kg is about 1.7 Billion paperclips. Keeping the world organized and fed. 📎",
      imperial: "3.8M lbs is about 1.7 Billion paperclips. Keeping the world organized and fed. 📎",
      theme: "global", scale: "micro" 
    },
    { 
      metric: "The weight of 280 African Elephants. We're talking mammoth impact here. 🐘",
      imperial: "The weight of 280 African Elephants. We're talking mammoth impact here. 🐘",
      theme: "global", scale: "medium" 
    },
    { 
      metric: "Equal to about 1/6th of the iron in the Eiffel Tower. C'est magnifique! 🗼",
      imperial: "Equal to about 1/6th of the iron in the Eiffel Tower. C'est magnifique! 🗼",
      theme: "global", scale: "macro" 
    },
    { 
      metric: "Equivalent to 8.5 million bananas. That's bananas! B-A-N-A-N-A-S. 🍌",
      imperial: "Equivalent to 8.5 million bananas. That's bananas! B-A-N-A-N-A-S. 🍌",
      theme: "global", scale: "micro" 
    },
    { 
      metric: "The weight of 2,400 Cows. But without the methane! 🐄",
      imperial: "The weight of 2,400 Cows. But without the methane! 🐄",
      theme: "global", scale: "medium" 
    },
    { 
      metric: "Heavier than a Giant Sequoia tree. Growing tall, literally and figuratively. 🌲",
      imperial: "Heavier than a Giant Sequoia tree. Growing tall, literally and figuratively. 🌲",
      theme: "global", scale: "macro" 
    },
    { 
      metric: "The weight of 113,000 gold bars. We think fresh food is the real gold standard. 🧱",
      imperial: "The weight of 113,000 gold bars. We think fresh food is the real gold standard. 🧱",
      theme: "global", scale: "micro" 
    }
  ],
  waterSaved: [
    // --- CANADIAN ---
    { 
      metric: "We saved 393M liters of water, enough to fill 1.15 billion bottles of beer on the wall. Take one down, pass it around! 🍺",
      imperial: "We saved 104M gallons of water, enough to fill 1.15 billion bottles of beer on the wall. Take one down, pass it around! 🍺",
      theme: "canadian", scale: "micro" 
    },
    { 
      metric: "You could flood the entire ice surface of an NHL rink... 3,200 times over with the water we saved! Game on! 🏒",
      imperial: "You could flood the entire ice surface of an NHL rink... 3,200 times over with the water we saved! Game on! 🏒",
      theme: "canadian", scale: "medium" 
    },
    { 
      metric: "That's 393M liters, roughly 3 minutes of the entire flow over Niagara Falls. A powerful drop in the bucket. 🌊",
      imperial: "That's 104M gallons, roughly 3 minutes of the entire flow over Niagara Falls. A powerful drop in the bucket. 🌊",
      theme: "canadian", scale: "macro" 
    },
    { 
      metric: "That's 1.5 Billion Double-Doubles worth of water saved. That's a whole lot of Timmies runs. ☕",
      imperial: "That's 1.5 Billion Double-Doubles worth of water saved. That's a whole lot of Timmies runs. ☕",
      theme: "canadian", scale: "micro" 
    },
    { 
      metric: "Enough to fill 1 billion standard bottles of Maple Syrup. But please don't put water on your pancakes. 🍁",
      imperial: "Enough to fill 1 billion standard bottles of Maple Syrup. But please don't put water on your pancakes. 🍁",
      theme: "canadian", scale: "medium" 
    },
    { 
      metric: "Enough water to fill the Rideau Canal Skateway... 4 times over! Sharpen your skates. ⛸️",
      imperial: "Enough water to fill the Rideau Canal Skateway... 4 times over! Sharpen your skates. ⛸️",
      theme: "canadian", scale: "macro" 
    },
    { 
      metric: "393M liters is enough to water every lawn in PEI for a week. Keeping the garden province green. 🏡",
      imperial: "104M gallons is enough to water every lawn in PEI for a week. Keeping the garden province green. 🏡",
      theme: "canadian", scale: "medium" 
    },
    { 
      metric: "Enough to fill nearly 800,000 Canoes to the brim. Paddle your own canoe! 🛶",
      imperial: "Enough to fill nearly 800,000 Canoes to the brim. Paddle your own canoe! 🛶",
      theme: "canadian", scale: "medium" 
    },
    { 
      metric: "Enough to fill the Rogers Centre dome to 25% capacity! Let's play ball (underwater)! ⚾",
      imperial: "Enough to fill the Rogers Centre dome to 25% capacity! Let's play ball (underwater)! ⚾",
      theme: "canadian", scale: "macro" 
    },
    { 
      metric: "Equivalent to 786 Million snowballs melted down. That's one epic snowball fight prevented. ❄️",
      imperial: "Equivalent to 786 Million snowballs melted down. That's one epic snowball fight prevented. ❄️",
      theme: "canadian", scale: "micro" 
    },

    // --- POP CULTURE ---
    { 
      metric: "That's enough to fill 786 million standard water bottles. Hydration is key, folks. 💧",
      imperial: "That's enough to fill 786 million standard water bottles. Hydration is key, folks. 💧",
      theme: "popCulture", scale: "micro" 
    },
    { 
      metric: "Enough to wash 390 million loads of laundry! Now if only it could fold the clothes too. 🧺",
      imperial: "Enough to wash 390 million loads of laundry! Now if only it could fold the clothes too. 🧺",
      theme: "popCulture", scale: "medium" 
    },
    { 
      metric: "You could fill the Las Vegas Sphere halfway to the top with our saved water! Viva Las Veggies. 🔮",
      imperial: "You could fill the Las Vegas Sphere halfway to the top with our saved water! Viva Las Veggies. 🔮",
      theme: "popCulture", scale: "macro" 
    },
    { 
      metric: "Equivalent to 6.5 Billion shots of espresso. We're jittery just thinking about it. ☕",
      imperial: "Equivalent to 6.5 Billion shots of espresso. We're jittery just thinking about it. ☕",
      theme: "popCulture", scale: "micro" 
    },
    { 
      metric: "Imagine 2.6 million bathtubs filled to the brim. That's how much water we saved. Scrub-a-dub-dub! 🛁",
      imperial: "Imagine 2.6 million bathtubs filled to the brim. That's how much water we saved. Scrub-a-dub-dub! 🛁",
      theme: "popCulture", scale: "medium" 
    },
    { 
      metric: "We saved enough water to fill the main tank at the Georgia Aquarium 16 times. Whale shark approved. 🦈",
      imperial: "We saved enough water to fill the main tank at the Georgia Aquarium 16 times. Whale shark approved. 🦈",
      theme: "popCulture", scale: "macro" 
    },
    { 
      metric: "Enough to make 1.3 Billion bowls of Ramen. College students rejoice! 🍜",
      imperial: "Enough to make 1.3 Billion bowls of Ramen. College students rejoice! 🍜",
      theme: "popCulture", scale: "micro" 
    },
    { 
      metric: "Fill the pool from the movie Project X 5,000 times. That's a party. 🏠",
      imperial: "Fill the pool from the movie Project X 5,000 times. That's a party. 🏠",
      theme: "popCulture", scale: "medium" 
    },
    { 
      metric: "Enough to fill the fountain at the Bellagio 20 times. Watch out for the water show! ⛲",
      imperial: "Enough to fill the fountain at the Bellagio 20 times. Watch out for the water show! ⛲",
      theme: "popCulture", scale: "macro" 
    },
    { 
      metric: "That's 1.1 Billion cans of Soda. We prefer water, though. 🥤",
      imperial: "That's 1.1 Billion cans of Soda. We prefer water, though. 🥤",
      theme: "popCulture", scale: "micro" 
    },

    // --- GLOBAL ---
    { 
      metric: "Equivalent to 65 million toilet flushes. We're flushing away water waste! 🚽",
      imperial: "Equivalent to 65 million toilet flushes. We're flushing away water waste! 🚽",
      theme: "global", scale: "micro" 
    },
    { 
      metric: "You could take 6.5 million 8-minute showers with the 393M liters we saved! Squeaky clean. 🚿",
      imperial: "You could take 6.5 million 8-minute showers with the 104M gallons we saved! Squeaky clean. 🚿",
      theme: "global", scale: "medium" 
    },
    { 
      metric: "That's enough water to fill 157 Olympic-sized swimming pools! Gold medal for conservation. 🏊‍♂️",
      imperial: "That's enough water to fill 157 Olympic-sized swimming pools! Gold medal for conservation. 🏊‍♂️",
      theme: "global", scale: "macro" 
    },
    { 
      metric: "Equivalent to 1.5 billion cups of tea. Pinkies up for sustainability! 🍵",
      imperial: "Equivalent to 1.5 billion cups of tea. Pinkies up for sustainability! 🍵",
      theme: "global", scale: "micro" 
    },
    { 
      metric: "Daily drinking water for the entire population of the UK... twice. Cheers mate! 🇬🇧",
      imperial: "Daily drinking water for the entire population of the UK... twice. Cheers mate! 🇬🇧",
      theme: "global", scale: "medium" 
    },
    { 
      metric: "Enough to fill the Trevi Fountain in Rome 1,300 times! Make a wish for the planet. ⛲",
      imperial: "Enough to fill the Trevi Fountain in Rome 1,300 times! Make a wish for the planet. ⛲",
      theme: "global", scale: "macro" 
    },
    { 
      metric: "Enough to fill 1 million garden ponds. The frogs are grateful. 🐸",
      imperial: "Enough to fill 1 million garden ponds. The frogs are grateful. 🐸",
      theme: "global", scale: "medium" 
    },
    { 
      metric: "The volume of 8 Titanics (displacement). That's a colossal amount of water. 🚢",
      imperial: "The volume of 8 Titanics (displacement). That's a colossal amount of water. 🚢",
      theme: "global", scale: "macro" 
    },
    { 
      metric: "Enough to wash 30 million cars. Shine 'em up! 🚗",
      imperial: "Enough to wash 30 million cars. Shine 'em up! 🚗",
      theme: "global", scale: "medium" 
    },
    { 
      metric: "That's 393 million Liters of water! Every drop counts in hydroponics. 💧",
      imperial: "That's 104 million Gallons of water! Every drop counts in hydroponics. 💧",
      theme: "global", scale: "micro" 
    }
  ],
  emissions: [
    // --- CANADIAN ---
    { 
      metric: "Equivalent to the CO2 sequestered by 75,000 mature Maple trees in a year. Tapping into sustainability. 🍁",
      imperial: "Equivalent to the CO2 sequestered by 75,000 mature Maple trees in a year. Tapping into sustainability. 🍁",
      theme: "canadian", scale: "micro" 
    },
    { 
      metric: "We avoided 1,660 tonnes of CO2, enough to heat 500 Canadian homes for a full winter. Stay warm! 🏠",
      imperial: "We avoided 1,830 tons of CO2, enough to heat 500 Canadian homes for a full winter. Stay warm! 🏠",
      theme: "canadian", scale: "medium" 
    },
    { 
      metric: "Like taking 360 gas-powered cars off the Trans-Canada Highway for an entire year. Road trip! 🚗",
      imperial: "Like taking 360 gas-powered cars off the Trans-Canada Highway for an entire year. Road trip! 🚗",
      theme: "canadian", scale: "macro" 
    },
    { 
      metric: "Equivalent to the emissions from 75,000 propane BBQs running dry! Burgers are done. 🌭",
      imperial: "Equivalent to the emissions from 75,000 propane BBQs running dry! Burgers are done. 🌭",
      theme: "canadian", scale: "medium" 
    },
    { 
      metric: "That amount of carbon is absorbed by the forest cover of 5 Stanley Parks. A walk in the park. 🌲",
      imperial: "That amount of carbon is absorbed by the forest cover of 5 Stanley Parks. A walk in the park. 🌲",
      theme: "canadian", scale: "macro" 
    },
    { 
      metric: "The weight of 3,300 Polar Bears... if they were made of gas! 🐻‍❄️",
      imperial: "The weight of 3,300 Polar Bears... if they were made of gas! 🐻‍❄️",
      theme: "canadian", scale: "medium" 
    },
    { 
      metric: "Equivalent to 250,000 campfire nights avoided. S'mores without the smoke. 🔥",
      imperial: "Equivalent to 250,000 campfire nights avoided. S'mores without the smoke. 🔥",
      theme: "canadian", scale: "micro" 
    },
    { 
      metric: "That avoids the emissions of over 5,500 one-way flights from Toronto to Vancouver! ✈️",
      imperial: "That avoids the emissions of over 5,500 one-way flights from Toronto to Vancouver! ✈️",
      theme: "canadian", scale: "macro" 
    },
    { 
      metric: "Emissions of 13,000 Snowmobiles running for a day. Braaap! ❄️",
      imperial: "Emissions of 13,000 Snowmobiles running for a day. Braaap! ❄️",
      theme: "canadian", scale: "medium" 
    },
    { 
      metric: "The carbon footprint of 2 million hockey games (arena power). He shoots, he scores for the planet! 🏒",
      imperial: "The carbon footprint of 2 million hockey games (arena power). He shoots, he scores for the planet! 🏒",
      theme: "canadian", scale: "micro" 
    },

    // --- POP CULTURE ---
    { 
      metric: "That's enough energy to charge 200 million smartphones! Can you hear me now? 📱",
      imperial: "That's enough energy to charge 200 million smartphones! Can you hear me now? 📱",
      theme: "popCulture", scale: "micro" 
    },
    { 
      metric: "You'd have to drive a DeLorean around the world 250 times to generate that much CO2! Great Scott! 🏎️",
      imperial: "You'd have to drive a DeLorean around the world 250 times to generate that much CO2! Great Scott! 🏎️",
      theme: "popCulture", scale: "medium" 
    },
    { 
      metric: "That offsets the emissions of launching a SpaceX Falcon 9... 4 times! To the moon! 🚀",
      imperial: "That offsets the emissions of launching a SpaceX Falcon 9... 4 times! To the moon! 🚀",
      theme: "popCulture", scale: "macro" 
    },
    { 
      metric: "The emissions of streaming a viral hit song 50 million times. Never gonna give you up... 🎧",
      imperial: "The emissions of streaming a viral hit song 50 million times. Never gonna give you up... 🎧",
      theme: "popCulture", scale: "micro" 
    },
    { 
      metric: "Heavier than the Millennium Falcon (assuming it weighs ~30 tons) x 55! Chewie, punch it! 🛸",
      imperial: "Heavier than the Millennium Falcon (assuming it weighs ~30 tons) x 55! Chewie, punch it! 🛸",
      theme: "popCulture", scale: "medium" 
    },
    { 
      metric: "Equivalent to the carbon footprint of 3,000 Rock Concerts. Rock on, sustainably. 🎸",
      imperial: "Equivalent to the carbon footprint of 3,000 Rock Concerts. Rock on, sustainably. 🎸",
      theme: "popCulture", scale: "macro" 
    },
    { 
      metric: "Emissions of mining 10 Bitcoin blocks. HODL the planet. 🪙",
      imperial: "Emissions of mining 10 Bitcoin blocks. HODL the planet. 🪙",
      theme: "popCulture", scale: "medium" 
    },
    { 
      metric: "The weight of 100,000 Clouds (small ones). Look up! ☁️",
      imperial: "The weight of 100,000 Clouds (small ones). Look up! ☁️",
      theme: "popCulture", scale: "macro" 
    },
    { 
      metric: "Enough gas to fill 300 million party balloons. Now that's a celebration. 🎈",
      imperial: "Enough gas to fill 300 million party balloons. Now that's a celebration. 🎈",
      theme: "popCulture", scale: "micro" 
    },
    { 
      metric: "Offsets the production of 500,000 sneakers. Just do it (greenly). 👟",
      imperial: "Offsets the production of 500,000 sneakers. Just do it (greenly). 👟",
      theme: "popCulture", scale: "medium" 
    },

    // --- GLOBAL ---
    { 
      metric: "The annual carbon footprint of 360 average global citizens. A global effort. 🌍",
      imperial: "The annual carbon footprint of 360 average global citizens. A global effort. 🌍",
      theme: "global", scale: "micro" 
    },
    { 
      metric: "That's 720,000 standard fire extinguishers worth of CO2! Safety first. 🧯",
      imperial: "That's 720,000 standard fire extinguishers worth of CO2! Safety first. 🧯",
      theme: "global", scale: "medium" 
    },
    { 
      metric: "We kept 1,660 metric tonnes of greenhouse gases out of the atmosphere. Breathing easier. ☁️",
      imperial: "We kept 1,830 US tons of greenhouse gases out of the atmosphere. Breathing easier. ☁️",
      theme: "global", scale: "macro" 
    },
    { 
      metric: "Equivalent to the carbon absorbed by 2,000 acres of forest. Tree-mendous impact. 🌳",
      imperial: "Equivalent to the carbon absorbed by 2,000 acres of forest. Tree-mendous impact. 🌳",
      theme: "global", scale: "medium" 
    },
    { 
      metric: "Equivalent to the weight of 2.6 Christ the Redeemer statues... made of pure CO2. 🇧🇷",
      imperial: "Equivalent to the weight of 2.6 Christ the Redeemer statues... made of pure CO2. 🇧🇷",
      theme: "global", scale: "macro" 
    },
    { 
      metric: "Volume-wise, that CO2 would fill 300 Hindenburg blimps! Oh the humanity! 🎈",
      imperial: "Volume-wise, that CO2 would fill 300 Hindenburg blimps! Oh the humanity! 🎈",
      theme: "global", scale: "macro" 
    },
    { 
      metric: "Offsets 10 million km of air travel. Fly less, grow local. ✈️",
      imperial: "Offsets 6.2 million miles of air travel. Fly less, grow local. ✈️",
      theme: "global", scale: "medium" 
    },
    { 
      metric: "Equivalent to burning 1.8 million pounds of coal. Keeping it in the ground. 🚂",
      imperial: "Equivalent to burning 1.8 million pounds of coal. Keeping it in the ground. 🚂",
      theme: "global", scale: "medium" 
    },
    { 
      metric: "Absorbed by 80,000 seedlings grown for 10 years. Growing strong. 🌱",
      imperial: "Absorbed by 80,000 seedlings grown for 10 years. Growing strong. 🌱",
      theme: "global", scale: "micro" 
    },
    { 
      metric: "The weight of 275 African Elephants in gas form. A heavy load lifted. 🐘",
      imperial: "The weight of 275 African Elephants in gas form. A heavy load lifted. 🐘",
      theme: "global", scale: "macro" 
    }
  ],
  foodMiles: [
    // --- CANADIAN ---
    { 
      metric: "Equivalent to skating the entire length of the Rideau Canal 1.3 million times. Don't forget your beavertail! ⛸️",
      imperial: "Equivalent to skating the entire length of the Rideau Canal 1.3 million times. Don't forget your beavertail! ⛸️",
      theme: "canadian", scale: "micro" 
    },
    { 
      metric: "That's like running Terry Fox's Marathon of Hope... 1,900 times! A true Canadian legacy. 🏃",
      imperial: "That's like running Terry Fox's Marathon of Hope... 1,900 times! A true Canadian legacy. 🏃",
      theme: "canadian", scale: "medium" 
    },
    { 
      metric: "We saved produce from traveling 10.2M km, equal to 250 trips around the Earth! 🌏",
      imperial: "We saved produce from traveling 6.3M miles, equal to 250 trips around the Earth! 🌏",
      theme: "canadian", scale: "macro" 
    },
    { 
      metric: "Like driving Yonge Street (the world's longest street) 5,300 times. That's a long commute. 🛣️",
      imperial: "Like driving Yonge Street (the world's longest street) 5,300 times. That's a long commute. 🛣️",
      theme: "canadian", scale: "medium" 
    },
    { 
      metric: "You could trace Canada's entire coastline (the world's longest) 42 times! From sea to sea to sea. 🗺️",
      imperial: "You could trace Canada's entire coastline (the world's longest) 42 times! From sea to sea to sea. 🗺️",
      theme: "canadian", scale: "macro" 
    },
    { 
      metric: "That's the distance of walking from St. John's to Victoria... 1,400 times! Bring good shoes. 🥾",
      imperial: "That's the distance of walking from St. John's to Victoria... 1,400 times! Bring good shoes. 🥾",
      theme: "canadian", scale: "medium" 
    },
    { 
      metric: "Equivalent to 100 Million hockey rinks laid end-to-end. Car! 🏒",
      imperial: "Equivalent to 100 Million hockey rinks laid end-to-end. Car! 🏒",
      theme: "canadian", scale: "micro" 
    },
    { 
      metric: "Equivalent to driving the entire Trans-Canada Highway 1,300 times. Life is a highway! 🇨🇦",
      imperial: "Equivalent to driving the entire Trans-Canada Highway 1,300 times. Life is a highway! 🇨🇦",
      theme: "canadian", scale: "macro" 
    },
    { 
      metric: "Climbing the CN Tower 18 Million times. Views for days. 🗼",
      imperial: "Climbing the CN Tower 18 Million times. Views for days. 🗼",
      theme: "canadian", scale: "medium" 
    },
    { 
      metric: "Length of 500 Million Beavers nose-to-tail. A dam big number. 🦫",
      imperial: "Length of 500 Million Beavers nose-to-tail. A dam big number. 🦫",
      theme: "canadian", scale: "micro" 
    },

    // --- POP CULTURE ---
    { 
      metric: "Like walking 500 miles (The Proclaimers)... 12,000 times! And I would walk 500 more. 🎵",
      imperial: "Like walking 500 miles (The Proclaimers)... 12,000 times! And I would walk 500 more. 🎵",
      theme: "popCulture", scale: "micro" 
    },
    { 
      metric: "You could run with Forrest Gump across the US 415 times! Run, Forrest, Run! 🏃‍♂️",
      imperial: "You could run with Forrest Gump across the US 415 times! Run, Forrest, Run! 🏃‍♂️",
      theme: "popCulture", scale: "medium" 
    },
    { 
      metric: "Travel the Rainbow Road (Mario Kart) 1.7 million times! Watch out for blue shells. 🏎️",
      imperial: "Travel the Rainbow Road (Mario Kart) 1.7 million times! Watch out for blue shells. 🏎️",
      theme: "popCulture", scale: "macro" 
    },
    { 
      metric: "That's like driving from The Shire to Mordor... 3 million times. One does not simply walk there. 🌋",
      imperial: "That's like driving from The Shire to Mordor... 3 million times. One does not simply walk there. 🌋",
      theme: "popCulture", scale: "medium" 
    },
    { 
      metric: "Equivalent to 26 one-way trips to the Moon... but with lettuce! One small step for salad. 🚀",
      imperial: "Equivalent to 26 one-way trips to the Moon... but with lettuce! One small step for salad. 🚀",
      theme: "popCulture", scale: "macro" 
    },
    { 
      metric: "Running the Death Star Trench 100,000 times. Use the Force. ⚔️",
      imperial: "Running the Death Star Trench 100,000 times. Use the Force. ⚔️",
      theme: "popCulture", scale: "medium" 
    },
    { 
      metric: "The length of 30 billion iPhone cables. And none of them are frayed. 🔌",
      imperial: "The length of 30 billion iPhone cables. And none of them are frayed. 🔌",
      theme: "popCulture", scale: "micro" 
    },
    { 
      metric: "Swimming Finding Nemo's journey 5,000 times. Just keep swimming! 🐠",
      imperial: "Swimming Finding Nemo's journey 5,000 times. Just keep swimming! 🐠",
      theme: "popCulture", scale: "macro" 
    },
    { 
      metric: "Walking the Yellow Brick Road 20,000 times. Follow the green brick road! 👠",
      imperial: "Walking the Yellow Brick Road 20,000 times. Follow the green brick road! 👠",
      theme: "popCulture", scale: "medium" 
    },
    { 
      metric: "Length of 800 million Subway footlongs. Eat fresh, literally. 🥪",
      imperial: "Length of 800 million Subway footlongs. Eat fresh, literally. 🥪",
      theme: "popCulture", scale: "micro" 
    },

    // --- GLOBAL ---
    { 
      metric: "You could walk the entire Great Wall of China 500 times. A monumental journey saved. 🧱",
      imperial: "You could walk the entire Great Wall of China 500 times. A monumental journey saved. 🧱",
      theme: "global", scale: "medium" 
    },
    { 
      metric: "North Pole to South Pole... 510 times. A polar trek avoided. 🐧",
      imperial: "North Pole to South Pole... 510 times. A polar trek avoided. 🐧",
      theme: "global", scale: "macro" 
    },
    { 
      metric: "You could circle the planet Jupiter 23 times! That's out of this world. 🪐",
      imperial: "You could circle the planet Jupiter 23 times! That's out of this world. 🪐",
      theme: "global", scale: "macro" 
    },
    { 
      metric: "That's 255 trips around the Equator! Around the world in 80 days? Try 255 times. 🗺️",
      imperial: "That's 255 trips around the Equator! Around the world in 80 days? Try 255 times. 🗺️",
      theme: "global", scale: "macro" 
    },
    { 
      metric: "That's 265,000 marathons run back-to-back. The ultimate endurance test. 👟",
      imperial: "That's 265,000 marathons run back-to-back. The ultimate endurance test. 👟",
      theme: "global", scale: "medium" 
    },
    { 
      metric: "Distance of 200,000 Tour de France races. Le Tour de Local. 🚲",
      imperial: "Distance of 200,000 Tour de France races. Le Tour de Local. 🚲",
      theme: "global", scale: "medium" 
    },
    { 
      metric: "Length of 1 Trillion Ants in a line. That's an army of ants. 🐜",
      imperial: "Length of 1 Trillion Ants in a line. That's an army of ants. 🐜",
      theme: "global", scale: "micro" 
    },
    { 
      metric: "Driving Route 66 over 2,500 times. Get your kicks on Route 66. 🛣️",
      imperial: "Driving Route 66 over 2,500 times. Get your kicks on Route 66. 🛣️",
      theme: "global", scale: "medium" 
    },
    { 
      metric: "Climbing Everest 1.1 Million times. The peak of local production. 🏔️",
      imperial: "Climbing Everest 1.1 Million times. The peak of local production. 🏔️",
      theme: "global", scale: "medium" 
    },
    { 
      metric: "Length of the Nile River 1,500 times. Denial is not a river in Egypt. 🐊",
      imperial: "Length of the Nile River 1,500 times. Denial is not a river in Egypt. 🐊",
      theme: "global", scale: "macro" 
    }
  ],
  servings: [
    // --- CANADIAN ---
    { 
      metric: "That's 1.2 daily vegetable servings for every single citizen in Canada! Coast to coast nutrition. 🥗",
      imperial: "That's 1.2 daily vegetable servings for every single citizen in Canada! Coast to coast nutrition. 🥗",
      theme: "canadian", scale: "macro" 
    },
    { 
      metric: "Imagine filling the Rogers Centre entirely with lettuce... 863 times! A home run for health. ⚾",
      imperial: "Imagine filling the Rogers Centre entirely with lettuce... 863 times! A home run for health. ⚾",
      theme: "canadian", scale: "macro" 
    },
    { 
      metric: "If servings were hockey pucks, we'd fill the Stanley Cup 4,000 times! Champions of greens. 🏆",
      imperial: "If servings were hockey pucks, we'd fill the Stanley Cup 4,000 times! Champions of greens. 🏆",
      theme: "canadian", scale: "medium" 
    },
    { 
      metric: "Enough greens to fill a canoe for every resident of Ottawa. Paddle and munch. 🛶",
      imperial: "Enough greens to fill a canoe for every resident of Ottawa. Paddle and munch. 🛶",
      theme: "canadian", scale: "medium" 
    },
    { 
      metric: "That's 5.7 Million boxes of Kraft Dinner (if it had veggies)! Gotta eat your greens. 🧀",
      imperial: "That's 5.7 Million boxes of Kraft Dinner (if it had veggies)! Gotta eat your greens. 🧀",
      theme: "canadian", scale: "micro" 
    },
    { 
      metric: "If you lined up the salad bowls, they would stretch from Toronto to Florida! Snowbirds love it. 📏",
      imperial: "If you lined up the salad bowls, they would stretch from Toronto to Florida! Snowbirds love it. 📏",
      theme: "canadian", scale: "macro" 
    },
    { 
      metric: "A salad for every moose, beaver, and goose in the country. The wildlife is well fed. 🦫",
      imperial: "A salad for every moose, beaver, and goose in the country. The wildlife is well fed. 🦫",
      theme: "canadian", scale: "macro" 
    },
    { 
      metric: "Enough to fill 200,000 recycling bins. But this goes on your plate, not the bin! ♻️",
      imperial: "Enough to fill 200,000 recycling bins. But this goes on your plate, not the bin! ♻️",
      theme: "canadian", scale: "medium" 
    },
    { 
      metric: "46 million servings... that's a lot of coleslaw. The BBQ is ready. 🥕",
      imperial: "46 million servings... that's a lot of coleslaw. The BBQ is ready. 🥕",
      theme: "canadian", scale: "micro" 
    },
    { 
      metric: "Equivalent to 3.8 Million Dozen Eggs. A balanced breakfast. 🥚",
      imperial: "Equivalent to 3.8 Million Dozen Eggs. A balanced breakfast. 🥚",
      theme: "canadian", scale: "micro" 
    },

    // --- POP CULTURE ---
    { 
      metric: "That's a side salad for every attendee of every Taylor Swift Eras Tour concert... 640 times over! Shake it off with a salad. 🎤",
      imperial: "That's a side salad for every attendee of every Taylor Swift Eras Tour concert... 640 times over! Shake it off with a salad. 🎤",
      theme: "popCulture", scale: "medium" 
    },
    { 
      metric: "One salad to rule them all? Actually 46 million of them. My preciousss greens. 💍",
      imperial: "One salad to rule them all? Actually 46 million of them. My preciousss greens. 💍",
      theme: "popCulture", scale: "micro" 
    },
    { 
      metric: "If you stacked every serving bowl, the tower would reach past the International Space Station! Out of this world flavor. 🛰️",
      imperial: "If you stacked every serving bowl, the tower would reach past the International Space Station! Out of this world flavor. 🛰️",
      theme: "popCulture", scale: "macro" 
    },
    { 
      metric: "Enough greens to fill 46 million taco shells! Taco Tuesday just got an upgrade. 🌮",
      imperial: "Enough greens to fill 46 million taco shells! Taco Tuesday just got an upgrade. 🌮",
      theme: "popCulture", scale: "micro" 
    },
    { 
      metric: "Feed every person at Coachella... for 368 years! Festival food revolution. 🎡",
      imperial: "Feed every person at Coachella... for 368 years! Festival food revolution. 🎡",
      theme: "popCulture", scale: "macro" 
    },
    { 
      metric: "A salad for every subscriber to MrBeast (well, 1/4 of them). Subscribe to health. ▶️",
      imperial: "A salad for every subscriber to MrBeast (well, 1/4 of them). Subscribe to health. ▶️",
      theme: "popCulture", scale: "macro" 
    },
    { 
      metric: "Enough to fill the belly of Godzilla. That's one satisfied kaiju. 🦖",
      imperial: "Enough to fill the belly of Godzilla. That's one satisfied kaiju. 🦖",
      theme: "popCulture", scale: "macro" 
    },
    { 
      metric: "A snack for every Minion (there's a lot). Banana? No, lettuce! 🍌",
      imperial: "A snack for every Minion (there's a lot). Banana? No, lettuce! 🍌",
      theme: "popCulture", scale: "medium" 
    },
    { 
      metric: "Enough to fill the Hogwarts Great Hall 500 times. 10 points to Gryffindor for nutrition. 🧙‍♂️",
      imperial: "Enough to fill the Hogwarts Great Hall 500 times. 10 points to Gryffindor for nutrition. 🧙‍♂️",
      theme: "popCulture", scale: "medium" 
    },
    { 
      metric: "46 Million servings is 1.5 times the population of Skyrim. Let me guess, someone stole your sweetroll? 🐉",
      imperial: "46 Million servings is 1.5 times the population of Skyrim. Let me guess, someone stole your sweetroll? 🐉",
      theme: "popCulture", scale: "medium" 
    },

    // --- GLOBAL ---
    { 
      metric: "That's a salad for every resident of Tokyo, NYC, and London combined. A global dinner party. 🏙️",
      imperial: "That's a salad for every resident of Tokyo, NYC, and London combined. A global dinner party. 🏙️",
      theme: "global", scale: "macro" 
    },
    { 
      metric: "Enough to cover Central Park in leafy greens 5 times over. A truly green space. 🌳",
      imperial: "Enough to cover Central Park in leafy greens 5 times over. A truly green space. 🌳",
      theme: "global", scale: "macro" 
    },
    { 
      metric: "You could fill the Roman Colosseum with greens 920 times! Are you not entertained? 🏛️",
      imperial: "You could fill the Roman Colosseum with greens 920 times! Are you not entertained? 🏛️",
      theme: "global", scale: "medium" 
    },
    { 
      metric: "A mountain of greens weighing as much as 8 Statues of Liberty! Liberty and lettuce for all. 🗽",
      imperial: "A mountain of greens weighing as much as 8 Statues of Liberty! Liberty and lettuce for all. 🗽",
      theme: "global", scale: "macro" 
    },
    { 
      metric: "Enough to provide a healthy snack for every person in California. Golden State greens. 🐻",
      imperial: "Enough to provide a healthy snack for every person in California. Golden State greens. 🐻",
      theme: "global", scale: "macro" 
    },
    { 
      metric: "Feed the entire population of Spain a side salad. Buen provecho! 🇪🇸",
      imperial: "Feed the entire population of Spain a side salad. Buen provecho! 🇪🇸",
      theme: "global", scale: "macro" 
    },
    { 
      metric: "46 million servings of Kimchi. Spicy and sustainable. 🥬",
      imperial: "46 million servings of Kimchi. Spicy and sustainable. 🥬",
      theme: "global", scale: "micro" 
    },
    { 
      metric: "Enough to fill 1,000 shipping containers. But ours stay local! 🚢",
      imperial: "Enough to fill 1,000 shipping containers. But ours stay local! 🚢",
      theme: "global", scale: "medium" 
    },
    { 
      metric: "Equivalent to 150 million tomatoes. You say tomato, we say lettuce. 🍅",
      imperial: "Equivalent to 150 million tomatoes. You say tomato, we say lettuce. 🍅",
      theme: "global", scale: "micro" 
    },
    { 
      metric: "A salad bowl line reaching from London to Rome. All roads lead to salad. 🗺️",
      imperial: "A salad bowl line reaching from London to Rome. All roads lead to salad. 🗺️",
      theme: "global", scale: "medium" 
    }
  ],
  wasteAvoided: [
    // --- CANADIAN ---
    { 
      metric: "That's roughly 97,000 Raccoons worth of food prevented from hitting the bin! The trash pandas are disappointed. 🦝",
      imperial: "That's roughly 97,000 Raccoons worth of food prevented from hitting the bin! The trash pandas are disappointed. 🦝",
      theme: "canadian", scale: "medium" 
    },
    { 
      metric: "Equivalent to the weight of 1,200 Moose. That's a massive amount of food saved. 🫎",
      imperial: "Equivalent to the weight of 1,200 Moose. That's a massive amount of food saved. 🫎",
      theme: "canadian", scale: "macro" 
    },
    { 
      metric: "Equivalent to 1.3 million blocks of butter. Smooth move on saving waste. 🧈",
      imperial: "Equivalent to 1.3 million blocks of butter. Smooth move on saving waste. 🧈",
      theme: "canadian", scale: "micro" 
    },
    { 
      metric: "The weight of 34,000 bags of hockey gear. And it smells much better. 🏒",
      imperial: "The weight of 34,000 bags of hockey gear. And it smells much better. 🏒",
      theme: "canadian", scale: "medium" 
    },
    { 
      metric: "You could fill 68 garbage trucks with that much produce. Keep on trucking, but not to the landfill. 🚛",
      imperial: "You could fill 68 garbage trucks with that much produce. Keep on trucking, but not to the landfill. 🚛",
      theme: "canadian", scale: "macro" 
    },
    { 
      metric: "That's 5,000 Black Bears worth of waste prevented! Bear-y impressive. 🐻",
      imperial: "That's 5,000 Black Bears worth of waste prevented! Bear-y impressive. 🐻",
      theme: "canadian", scale: "medium" 
    },
    { 
      metric: "Equivalent to the weight of 136,000 Beaver Tails! A sweet victory over waste. 🍩",
      imperial: "Equivalent to the weight of 136,000 Beaver Tails! A sweet victory over waste. 🍩",
      theme: "canadian", scale: "micro" 
    },
    { 
      metric: "Heavier than a locomotive engine. Choo choo choose to reduce waste. 🚂",
      imperial: "Heavier than a locomotive engine. Choo choo choose to reduce waste. 🚂",
      theme: "canadian", scale: "macro" 
    },
    { 
      metric: "Weight of 100,000 Winter Tires. Grip the road to sustainability. 🚗",
      imperial: "Weight of 100,000 Winter Tires. Grip the road to sustainability. 🚗",
      theme: "canadian", scale: "medium" 
    },
    { 
      metric: "3.5 Million Hockey Pucks worth of waste. Shutout for the landfill! 🏒",
      imperial: "3.5 Million Hockey Pucks worth of waste. Shutout for the landfill! 🏒",
      theme: "canadian", scale: "micro" 
    },

    // --- POP CULTURE ---
    { 
      metric: "Imagine a pile of wasted food the size of a small skyscraper... gone! We're reaching new heights. 🏢",
      imperial: "Imagine a pile of wasted food the size of a small skyscraper... gone! We're reaching new heights. 🏢",
      theme: "popCulture", scale: "macro" 
    },
    { 
      metric: "Heavier than the cast of Star Wars combined... times 100. The Force is strong with this one. ✨",
      imperial: "Heavier than the cast of Star Wars combined... times 100. The Force is strong with this one. ✨",
      theme: "popCulture", scale: "medium" 
    },
    { 
      metric: "That's the weight of about 1,700 Grand Pianos! A grand achievement. 🎹",
      imperial: "That's the weight of about 1,700 Grand Pianos! A grand achievement. 🎹",
      theme: "popCulture", scale: "medium" 
    },
    { 
      metric: "That's the weight of 85,000 bowling balls! Strike out food waste! 🎳",
      imperial: "That's the weight of 85,000 bowling balls! Strike out food waste! 🎳",
      theme: "popCulture", scale: "micro" 
    },
    { 
      metric: "Equivalent to 480 Tesla Model 3s worth of food. Electrifying efficiency. 🚗",
      imperial: "Equivalent to 480 Tesla Model 3s worth of food. Electrifying efficiency. 🚗",
      theme: "popCulture", scale: "medium" 
    },
    { 
      metric: "Heavier than Godzilla (approx 20,000 tons... actually we are 3% of Godzilla). King of conservation. 🦖",
      imperial: "Heavier than Godzilla (approx 20,000 tons... actually we are 3% of Godzilla). King of conservation. 🦖",
      theme: "popCulture", scale: "macro" 
    },
    { 
      metric: "Weight of 136,000 lightsabers. A Jedi uses produce for knowledge and defense. ⚔️",
      imperial: "Weight of 136,000 lightsabers. A Jedi uses produce for knowledge and defense. ⚔️",
      theme: "popCulture", scale: "micro" 
    },
    { 
      metric: "Equivalent to 40,000 R2-D2 units. Beep boop, waste reduced. 🤖",
      imperial: "Equivalent to 40,000 R2-D2 units. Beep boop, waste reduced. 🤖",
      theme: "popCulture", scale: "medium" 
    },
    { 
      metric: "Heavier than the house from Up. Adventure is out there, waste is not. 🎈",
      imperial: "Heavier than the house from Up. Adventure is out there, waste is not. 🎈",
      theme: "popCulture", scale: "macro" 
    },
    { 
      metric: "Weight of 3.5 million Golden Snitches. 150 points for reducing waste! 🧹",
      imperial: "Weight of 3.5 million Golden Snitches. 150 points for reducing waste! 🧹",
      theme: "popCulture", scale: "micro" 
    },

    // --- GLOBAL ---
    { 
      metric: "The weight of 110 African Elephants. That's a jumbo reduction. 🐘",
      imperial: "The weight of 110 African Elephants. That's a jumbo reduction. 🐘",
      theme: "global", scale: "macro" 
    },
    { 
      metric: "Feed a stadium of 60,000 people for a week with the food we saved. Go team! 🏟️",
      imperial: "Feed a stadium of 60,000 people for a week with the food we saved. Go team! 🏟️",
      theme: "global", scale: "medium" 
    },
    { 
      metric: "That's 5.7 Million Bananas saved! No slipping up here. 🍌",
      imperial: "That's 5.7 Million Bananas saved! No slipping up here. 🍌",
      theme: "global", scale: "micro" 
    },
    { 
      metric: "Prevented waste equal to the weight of 4 Boeing 747s! Flying high on efficiency. ✈️",
      imperial: "Prevented waste equal to the weight of 4 Boeing 747s! Flying high on efficiency. ✈️",
      theme: "global", scale: "macro" 
    },
    { 
      metric: "Enough saved food to pack 1.3 million lunchboxes! School's in session. 🍱",
      imperial: "Enough saved food to pack 1.3 million lunchboxes! School's in session. 🍱",
      theme: "global", scale: "micro" 
    },
    { 
      metric: "Weight of 10 Space Shuttles. Waste reduction is not rocket science. 🚀",
      imperial: "Weight of 10 Space Shuttles. Waste reduction is not rocket science. 🚀",
      theme: "global", scale: "macro" 
    },
    { 
      metric: "Equivalent to 3 million apples. How do you like them apples? 🍎",
      imperial: "Equivalent to 3 million apples. How do you like them apples? 🍎",
      theme: "global", scale: "micro" 
    },
    { 
      metric: "The weight of 7,000 Kangaroos. Hop to it! 🦘",
      imperial: "The weight of 7,000 Kangaroos. Hop to it! 🦘",
      theme: "global", scale: "medium" 
    },
    { 
      metric: "Heavier than the statue of Christ the Redeemer. A monumental saving. 🇧🇷",
      imperial: "Heavier than the statue of Christ the Redeemer. A monumental saving. 🇧🇷",
      theme: "global", scale: "macro" 
    },
    { 
      metric: "Weight of 1,000 Camels. Avoiding the straw that broke the camel's back. 🐪",
      imperial: "Weight of 1,000 Camels. Avoiding the straw that broke the camel's back. 🐪",
      theme: "global", scale: "medium" 
    }
  ]
};

const formatData = (system) => {
  const isMetric = system === 'metric';
  const fmt = (num) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 1, notation: "compact" }).format(num);

  return {
    activeFarms: "421",
    yield: fmt(RAW_DATA.yield) + " units",
    servings: fmt(RAW_DATA.servings),
    students: fmt(RAW_DATA.students),
    meals: fmt(RAW_DATA.meals),
    jobsCreated: RAW_DATA.jobsCreated,
    freshFood: isMetric ? fmt(RAW_DATA.freshFood) + " kg" : fmt(RAW_DATA.freshFood * 2.20462) + " lbs",
    waterSaved: isMetric ? fmt(RAW_DATA.waterSaved) + " L" : fmt(RAW_DATA.waterSaved * 0.264172) + " gal",
    wasteAvoided: isMetric ? fmt(RAW_DATA.wasteAvoided) + " kg" : fmt(RAW_DATA.wasteAvoided * 2.20462) + " lbs",
    foodMiles: isMetric ? fmt(RAW_DATA.foodMiles) + " km" : fmt(RAW_DATA.foodMiles * 0.621371) + " miles",
    emissions: isMetric ? fmt(RAW_DATA.emissions) + " kg CO₂e" : fmt(RAW_DATA.emissions * 2.20462) + " lbs CO₂e"
  };
};

/* --- Tooltips Configuration --- */
const TOOLTIPS = {
  production: {
    title: "Production Methodology",
    content: (
      <>
        <p className="mb-2"><strong>Yield Calculation:</strong> Based on weekly yield of 650 units per farm, with an average weight of 120g per unit.</p>
        <p className="mb-2"><strong>Servings:</strong> Converted from total yield using Canada Health guidance of <strong>37g per serving</strong>.</p>
        <p className="mb-2"><strong>Capacity Note:</strong> A Growcer or Freight Farm can produce up to <strong>900 heads of greens per week</strong> if used at full capacity.</p>
        <p><strong>Peak Freshness:</strong> Harvest-to-table in hours, not days, ensuring superior nutrition and taste.</p>
      </>
    )
  },
  community: {
    title: "Community Reach Calculation",
    content: (
      <>
        <p className="mb-2"><strong>Students Reached:</strong> We count the entire student population of verified educational institutions operating our farms.</p>
        <p><strong>Meals & Hampers:</strong> We account for the total meals or hampers served by organizations using our farms (e.g., food banks, hospitals, community programs).</p>
      </>
    )
  },
  water: {
    title: "Water Savings Methodology",
    content: (
      <>
        <p className="mb-2">Calculated using peer-reviewed research comparing:</p>
        <ul className="list-disc pl-4 mb-2 space-y-1">
          <li>Traditional Agriculture: <strong>250L/kg</strong> (66 gal/lb)</li>
          <li>Recirculating Hydroponics: <strong>20L/kg</strong> (5.3 gal/lb)</li>
        </ul>
        <p><strong>Total Savings:</strong> 393M liters (104M gallons).</p>
      </>
    )
  },
  waste: {
    title: "Food Waste Prevention",
    content: (
      <>
        <p className="mb-2"><strong>Extended Shelf Life:</strong> Produce stays fresh for up to a month vs. 3-7 days for transported greens.</p>
        <p className="mb-2">This prevents ~30-40% of food waste compared to traditional supply chains.</p>
        <p><strong>Impact:</strong> Avoids 683,000kg (1.5M lbs) of waste and prevents methane emissions from decomposition.</p>
      </>
    )
  },
  carbon: {
    title: "Combined Environmental Impact",
    content: (
      <>
        <p className="mb-2"><strong>Zero Food Miles:</strong> Traditional produce travels ~2,400km (1,491 miles) on average. Our hyperlocal production eliminates this long-haul transport entirely.</p>
        <p className="mb-2"><strong>Total Emissions Avoided:</strong> 1,660 metric tonnes (1,829 US tons) of CO₂e annually through:</p>
        <ul className="list-disc pl-4 space-y-1">
            <li>Eliminated Transportation</li>
            <li>Prevented Waste Methane</li>
        </ul>
      </>
    )
  }
};

/* --- Components --- */

const Card = ({ children, className = "", tooltipKey, style }) => {
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const tooltipData = tooltipKey ? TOOLTIPS[tooltipKey] : null;

  return (
    <div 
      className={`bg-white border rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 relative group overflow-hidden ${className}`} 
      style={{ borderColor: BRAND.sage, ...style }}
    >
      {/* Content Layer */}
      <div className="relative z-10">
        {/* Tooltip Icon Toggle */}
        {tooltipData && (
          <button 
            onClick={() => setIsOverlayOpen(true)}
            className="absolute top-0 right-0 z-20 p-1.5 rounded-full hover:bg-slate-100 transition-colors text-slate-300 hover:text-green-700"
            aria-label="View methodology"
          >
            <Info size={20} />
          </button>
        )}
        {children}
      </div>

      {/* Slide-Up Overlay Layer */}
      {tooltipData && (
        <div 
          className={`absolute inset-0 bg-slate-900/95 z-40 transition-transform duration-500 ease-out flex flex-col p-6 text-left overflow-y-auto ${isOverlayOpen ? 'translate-y-0' : 'translate-y-full'}`}
        >
            <div className="font-bold text-sm mb-3 uppercase tracking-wide border-b border-white/20 pb-2 flex items-center justify-between" style={{ color: BRAND.lime }}>
                <span>{tooltipData.title}</span>
                <button 
                  onClick={(e) => { e.stopPropagation(); setIsOverlayOpen(false); }}
                  className="text-slate-400 hover:text-white transition-colors p-1"
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
            </div>
            <div className="text-slate-300 text-xs leading-relaxed font-brand space-y-2">
                {tooltipData.content}
            </div>
             <div className="mt-auto pt-4 flex items-center gap-2 text-[10px] uppercase font-bold tracking-wider" style={{ color: BRAND.secondary }}>
                <Check size={12} /> Verified Methodology
             </div>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, subtext, icon: Icon, delay = 0 }) => {
  const [show, setShow] = useState(false);
  useEffect(() => { const timer = setTimeout(() => setShow(true), delay); return () => clearTimeout(timer); }, [delay]);

  return (
    <div className={`relative transform transition-all duration-700 ${show ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
      <div className="flex items-start justify-between mb-3 relative z-10">
        <div className="p-3 rounded-xl transition-colors bg-opacity-10" style={{ backgroundColor: BRAND.cream, color: BRAND.primary }}>
          <Icon size={24} strokeWidth={2} />
        </div>
      </div>
      <h3 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-1 font-brand break-words" style={{ color: BRAND.primary }}>{value}</h3>
      <p className="font-medium font-brand text-sm uppercase tracking-wide opacity-80" style={{ color: BRAND.secondary }}>{label}</p>
      {subtext && <p className="text-xs mt-2 font-mono font-medium" style={{ color: BRAND.secondary }}>{subtext}</p>}
    </div>
  );
};

/* --- Impact Generator (Strict Rotation S-M-L) --- */

const ImpactGenerator = ({ currentData, unitSystem }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');
  const [selectedMetric, setSelectedMetric] = useState('waterSaved');
  const [analogyTheme, setAnalogyTheme] = useState('canadian');
  
  // Deterministic Counters for Rotation (S-M-L)
  const [globalRotationIndex, setGlobalRotationIndex] = useState(0); 

  // Initialize with a default analogy
  useEffect(() => {
    generateAnalogy();
  }, []);

  // Re-generate if unit system changes to update the text immediately
  useEffect(() => {
     generateAnalogy(true); // true = don't advance index, just update text
  }, [unitSystem]);

  const generateAnalogy = (updateOnly = false) => {
    setLoading(true);
    
    setTimeout(() => {
      // 1. Get all analogies for the selected metric
      const allOptions = ANALOGIES[selectedMetric] || [];
      
      // 2. Filter by selected theme
      const themeOptions = allOptions.filter(a => a.theme === analogyTheme);
      
      // 3. Fallback to all if theme has no options (safety)
      const pool = themeOptions.length > 0 ? themeOptions : allOptions;
      
      // 4. Determine Target Scale: Micro -> Medium -> Macro
      // If we are just updating the text (unit switch), use the previous index
      const currentIndex = updateOnly ? (globalRotationIndex > 0 ? globalRotationIndex - 1 : 0) : globalRotationIndex;
      
      const scales = ['micro', 'medium', 'macro'];
      const currentScale = scales[currentIndex % 3];
      
      // 5. Get candidates for this scale
      const candidates = pool.filter(a => a.scale === currentScale);
      
      let match;
      if (candidates.length > 0) {
        // Rotate through the candidates based on how many full S-M-L cycles we've done
        const cycleIndex = Math.floor(currentIndex / 3);
        match = candidates[cycleIndex % candidates.length];
      } else {
        // Fallback: If no candidates for scale
        match = pool[currentIndex % pool.length];
      }
      
      // 6. Select text based on Unit System
      const text = unitSystem === 'metric' ? match.metric : match.imperial;
      
      setResult(text);
      if (!updateOnly) {
          setGlobalRotationIndex(prev => prev + 1); // Increment for next click
      }
      setLoading(false);
    }, updateOnly ? 50 : 400); 
  };

  const copyToClipboard = () => {
    const textArea = document.createElement("textarea");
    textArea.value = result;
    textArea.style.top = "0"; textArea.style.left = "0"; textArea.style.position = "fixed";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      const successful = document.execCommand('copy');
      if (successful) { setCopied(true); setTimeout(() => setCopied(false), 2000); }
    } catch (err) {}
    document.body.removeChild(textArea);
  };
  
  const [copied, setCopied] = useState(false);

  return (
    <div className="w-full max-w-4xl mx-auto mb-16 font-brand">
      <div className="border rounded-2xl overflow-hidden shadow-xl relative bg-white" style={{ borderColor: BRAND.sage }}>
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r" style={{ backgroundImage: `linear-gradient(to right, ${BRAND.primary}, ${BRAND.secondary}, ${BRAND.turquoise})` }} />
        
        <div className="p-6 md:p-8">
          
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg shadow-sm animate-pulse text-white" style={{ background: `linear-gradient(to bottom right, ${BRAND.primary}, ${BRAND.secondary})` }}>
                <Sparkles size={20} />
              </div>
              <div>
                <h2 className="text-xl font-bold" style={{ color: BRAND.primary }}>Impact Perspective</h2>
                <p className="text-sm opacity-70" style={{ color: BRAND.secondary }}>Visualize the scale of our 2025 impact</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border bg-amber-50 border-amber-200 text-amber-800">
              <AlertCircle size={14} />
              <span>Verified data from 421 active farms.</span>
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-6">
            <div className="w-full md:w-1/3 space-y-4">
              <div className="space-y-3">
                <div>
                   <label className="text-[10px] font-bold uppercase tracking-wider opacity-70 mb-1 block" style={{ color: BRAND.primary }}>Metric</label>
                   <select 
                    value={selectedMetric}
                    onChange={(e) => {
                      setSelectedMetric(e.target.value);
                      setTimeout(() => document.getElementById('gen-btn').click(), 100); 
                    }}
                    className="w-full border rounded-lg p-3 text-sm focus:ring-2 focus:border-transparent outline-none bg-slate-50"
                    style={{ borderColor: BRAND.sage, color: BRAND.primary }}
                  >
                    <option value="servings">🥗 Veggie Servings ({currentData.servings})</option>
                    <option value="freshFood">🥬 Fresh Food Weight ({currentData.freshFood})</option>
                    <option value="waterSaved">💧 Water Avoided ({currentData.waterSaved})</option>
                    <option value="emissions">💨 Emissions Avoided ({currentData.emissions})</option>
                    <option value="foodMiles">🚚 Food Miles Saved ({currentData.foodMiles})</option>
                    <option value="wasteAvoided">🗑️ Waste Avoided ({currentData.wasteAvoided})</option>
                  </select>
                </div>
                
                {/* Theme Selector */}
                <div>
                   <label className="text-[10px] font-bold uppercase tracking-wider opacity-70 mb-1 block" style={{ color: BRAND.primary }}>Analogy Theme</label>
                   <select 
                    value={analogyTheme}
                    onChange={(e) => {
                      setAnalogyTheme(e.target.value);
                      setTimeout(() => document.getElementById('gen-btn').click(), 100); 
                    }}
                    className="w-full border rounded-lg p-3 text-sm focus:ring-2 focus:border-transparent outline-none bg-slate-50"
                    style={{ borderColor: BRAND.sage, color: BRAND.primary }}
                  >
                    <option value="canadian">🇨🇦 Canadian Context</option>
                    <option value="popCulture">🍿 Pop Culture</option>
                    <option value="global">🌎 Environment / Global</option>
                  </select>
                </div>

                <button 
                  id="gen-btn"
                  onClick={() => generateAnalogy(false)}
                  disabled={loading}
                  className="w-full py-3 rounded-lg font-bold transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 flex items-center justify-center gap-2 text-white mt-2"
                  style={{ backgroundColor: BRAND.primary }}
                >
                  {loading ? <Loader2 className="animate-spin" size={18} /> : <RefreshCw size={18} />}
                  Shuffle (Zoom In/Out)
                </button>
              </div>
            </div>

            <div className="w-full md:w-2/3 rounded-xl border p-6 relative min-h-[160px] flex flex-col bg-slate-50 justify-center" style={{ borderColor: BRAND.sage }}>
               <div className="absolute top-3 right-3 text-[10px] uppercase font-bold tracking-wider opacity-40">Did you know?</div>
              {result ? (
                <>
                  <div className="flex-grow font-brand text-lg leading-relaxed animate-in fade-in duration-500 italic flex items-center" style={{ color: BRAND.primary }}>
                    "{result}"
                  </div>
                  <div className="mt-4 flex justify-end items-center border-t pt-4 w-full" style={{ borderColor: BRAND.sage }}>
                    <button 
                      onClick={copyToClipboard}
                      className="text-xs font-bold flex items-center gap-2 transition-colors hover:opacity-80"
                      style={{ color: BRAND.secondary }}
                    >
                      {copied ? <Check size={14} /> : <Copy size={14} />}
                      {copied ? "Copied!" : "Copy Text"}
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-full gap-3 opacity-40" style={{ color: BRAND.primary }}>
                   <Loader2 className="animate-spin" size={32} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* --- Footer with Toggle --- */
const Footer = () => {
  const [showFullDisclaimer, setShowFullDisclaimer] = useState(false);

  return (
    <div className="text-center mt-16 p-8 border-t" style={{ borderColor: BRAND.sage, color: BRAND.primary }}>
      <div className="max-w-4xl mx-auto text-xs leading-relaxed opacity-80 text-justify">
        <h4 className="font-extrabold mb-4 text-sm uppercase tracking-wider text-center" style={{ color: BRAND.primary }}>2025 Growcer Impact Report - Legal Disclaimer</h4>
        
        <p className="mb-2">
          These figures represent annual impact from our tracked active farms only. All calculations are based on peer-reviewed research, government dietary guidelines, and conservative industry benchmarks. To calculate this data, we rely on a combination of customer-supplied data, aggregate data collected by Growcer, and inferences based on typical operating conditions of farms in our network.
        </p>

        {showFullDisclaimer && (
          <div className="animate-in fade-in slide-in-from-top-2 duration-300">
            <p className="mb-2">
              While best efforts were made to ensure the accuracy and reasonableness of the data presented, this report is provided for general informational purposes only and Growcer makes no representations or warranties, express or implied, as to the completeness, accuracy, reliability, or suitability of the information, estimates, or projections contained herein. Impact figures and projections are estimates and may differ materially from actual results. Results depend on numerous factors outside Growcer’s control, including but not limited to farm management practices, local conditions, crop selection, and market or regulatory changes.
            </p>
            <p className="mb-2">
              Past performance or estimated outcomes are no guarantee of future results. All assumptions, methodologies, and data sources are subject to change without notice. Nothing in this report should be construed as a solicitation, offer, endorsement, or recommendation by Growcer for any financial, operational, agricultural, nutritional, or business decisions. Growcer expressly disclaims any liability for decisions made or actions taken based on this information. Sources, methodologies, and underlying data are available upon request. By using this report, the reader acknowledges and agrees that Growcer is not responsible for any direct or indirect losses arising out of the use of this information.
            </p>
          </div>
        )}

        <div className="text-center mt-4">
          <button 
            onClick={() => setShowFullDisclaimer(!showFullDisclaimer)}
            className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider hover:opacity-70 transition-opacity"
            style={{ color: BRAND.secondary }}
          >
            {showFullDisclaimer ? (
              <>Read Less <ChevronUp size={14} /></>
            ) : (
              <>Read Full Disclaimer <ChevronDown size={14} /></>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

/* --- Main Dashboard View --- */
const DashboardView = () => {
    const [unitSystem, setUnitSystem] = useState('metric');
    const reportData = formatData(unitSystem);

    return (
        <div className="relative max-w-6xl mx-auto px-4 py-12 sm:px-6 lg:px-8 font-brand">
        
        {/* Toggle & Header Section */}
        <div className="flex justify-end mb-4">
           <button 
            onClick={() => setUnitSystem(prev => prev === 'metric' ? 'imperial' : 'metric')}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold border transition-all hover:bg-slate-100"
            style={{ borderColor: BRAND.sage, color: BRAND.primary }}
           >
             <Settings size={14} />
             {unitSystem === 'metric' ? 'Switch to Imperial (lbs/gal)' : 'Switch to Metric (kg/L)'}
           </button>
        </div>

        <div className={`text-center mb-12 animate-in fade-in slide-in-from-bottom-8 duration-700`}>
          <div className="inline-block px-4 py-1.5 rounded-full border text-sm font-bold mb-6 tracking-wide" style={{ backgroundColor: BRAND.lime, borderColor: BRAND.secondary, color: BRAND.primary }}>
            2025 IMPACT REPORT
          </div>
          <h1 className="text-6xl md:text-8xl font-black tracking-tighter mb-6" style={{ color: BRAND.primary }}>
            Growcer<br />Wrapped
          </h1>
          <p className="text-xl max-w-2xl mx-auto mb-8 font-medium leading-relaxed" style={{ color: BRAND.secondary }}>
            Scaling local food production across the globe. Here is what our network achieved together this year.
          </p>
        </div>

        {/* Impact Generator (No AI Key Needed) */}
        <ImpactGenerator currentData={reportData} unitSystem={unitSystem} />

        {/* The Deployment Stat (Transparency Section) */}
        <div className="mb-12">
          <Card className="bg-gradient-to-br from-white to-slate-50">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-3xl font-bold mb-4 flex items-center gap-3" style={{ color: BRAND.primary }}>
                  <Globe strokeWidth={2.5} style={{ color: BRAND.turquoise }} /> Global Footprint
                </h2>
                <p className="leading-relaxed mb-6 text-lg" style={{ color: BRAND.secondary }}>
                  Our network has grown to over <span className="font-bold border-b-2" style={{ color: BRAND.primary, borderColor: BRAND.lime }}>1,000 farms</span> deployed cumulatively across <span className="font-bold border-b-2" style={{ color: BRAND.primary, borderColor: BRAND.lime }}>30 countries</span>. 
                </p>
                
                {/* Transparency Info Box */}
                <div className="text-sm p-5 rounded-lg border-l-4" style={{ backgroundColor: BRAND.cream, borderColor: BRAND.primary, borderLeftColor: BRAND.secondary, color: BRAND.primary }}>
                  <div className="font-bold mb-2 uppercase tracking-wide text-xs opacity-70">Data Scope & Limitations</div>
                  <p className="mb-2"><strong>Active Status:</strong> We classify farms as "active" if they have a Farmhand subscription or we have confirmed operational status.</p>
                  <p><strong>Note:</strong> Many farms in the broader network are not included due to limited visibility. The actual global impact is likely significantly higher.</p>
                </div>

              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-6 rounded-xl border shadow-sm text-center" style={{ backgroundColor: BRAND.white, borderColor: BRAND.sage }}>
                  <div className="text-4xl font-extrabold mb-2" style={{ color: BRAND.primary }}>1,000+</div>
                  <div className="text-sm font-bold uppercase tracking-wide" style={{ color: BRAND.secondary }}>Total Deployed (All Time)</div>
                </div>
                <div className="p-6 rounded-xl border-2 shadow-sm text-center relative overflow-hidden group" style={{ backgroundColor: BRAND.cream, borderColor: BRAND.secondary }}>
                   <div className="absolute top-0 right-0 p-1 bg-secondary text-white text-[10px] font-bold px-2 rounded-bl-lg" style={{ backgroundColor: BRAND.secondary }}>VERIFIED 2025</div>
                  <div className="text-4xl font-extrabold mb-2" style={{ color: BRAND.primary }}>{reportData.activeFarms}</div>
                  <div className="text-sm font-bold uppercase tracking-wide" style={{ color: BRAND.secondary }}>Active Reporting Farms</div>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Main Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          
          {/* Production Stats */}
          <Card className="lg:col-span-2" tooltipKey="production">
            <div className="flex items-center gap-2 mb-8 border-b pb-4" style={{ borderColor: BRAND.cream }}>
              <Leaf style={{ color: BRAND.secondary }} strokeWidth={2.5} />
              <h2 className="text-xl font-bold" style={{ color: BRAND.primary }}>Production Power of our Network</h2>
            </div>
            <div className="grid sm:grid-cols-3 gap-8">
              <Stat 
                label="Total Produce Yield" 
                value={reportData.yield}
                subtext="Heads of greens"
                icon={Factory} 
                delay={200}
              />
              <Stat 
                label="Fresh Food Volume" 
                value={reportData.freshFood}
                subtext="Local food produced"
                icon={TrendingUp} 
                delay={300}
              />
              <Stat 
                label="Total Servings" 
                value={reportData.servings}
                subtext="Healthy portions"
                icon={Utensils} 
                delay={400}
              />
            </div>
          </Card>

          {/* Community Stats */}
          <Card className="lg:row-span-2 flex flex-col justify-center" style={{ background: `linear-gradient(to bottom, ${BRAND.white}, ${BRAND.cream})` }} tooltipKey="community">
             <div className="flex items-center gap-2 mb-8 border-b pb-4" style={{ borderColor: BRAND.sage }}>
              <Users style={{ color: BRAND.turquoise }} strokeWidth={2.5} />
              <h2 className="text-xl font-bold" style={{ color: BRAND.primary }}>Community Reach</h2>
            </div>
            <div className="space-y-12">
               <Stat 
                label="Students Reached" 
                value={reportData.students} 
                icon={GraduationCap} 
                delay={500}
              />
               <Stat 
                label="Meals/Hampers Impacted" 
                value={reportData.meals}
                icon={Utensils} 
                delay={600}
              />
            </div>
          </Card>

          {/* Environmental Stats */}
          <Card tooltipKey="water">
            <Stat 
              label="Water Avoided" 
              value={reportData.waterSaved}
              subtext="Vs. traditional ag"
              icon={Droplets} 
              delay={800}
            />
          </Card>
          
          <Card tooltipKey="waste">
             <Stat 
              label="Food Waste Avoided" 
              value={reportData.wasteAvoided}
              subtext="Volume preserved"
              icon={Trash2} 
              delay={900}
            />
          </Card>

           <Card className="lg:col-span-2" tooltipKey="carbon">
            <div className="flex items-center gap-2 mb-8 border-b pb-4" style={{ borderColor: BRAND.cream }}>
              <Wind style={{ color: BRAND.turquoise }} strokeWidth={2.5} />
              <h2 className="text-xl font-bold" style={{ color: BRAND.primary }}>Carbon Footprint</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-8">
               <Stat 
                label="Food Miles Saved" 
                value={reportData.foodMiles}
                icon={Globe} 
                delay={1000}
              />
              <Stat 
                label="GHG Emissions Avoided" 
                value={reportData.emissions} 
                subtext="Transport + Waste Methane"
                icon={Leaf} 
                delay={1100}
              />
            </div>
          </Card>

        </div>
        
        {/* New Interactive Footer */}
        <Footer />

      </div>
    );
};

/* --- Main App Wrapper --- */

export default function App() {
  return (
    <div className="min-h-screen font-brand overflow-x-hidden" style={{ backgroundColor: BRAND.cream, color: BRAND.primary }}>
      <FontStyles />
      <DashboardView />
    </div>
  );
}
